import { useCallback, useMemo } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, styled } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider, useFieldArray, useFormState } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { hasActiveHazardCargo } from "./truckEntryDateSync";
import type { TruckGatepassOutletContext } from "./types";

const TRANSLATION_KEY = "gatepass";
const SHOW_MULTI_CARGO_CONTROLS = true;

type GenericReferenceItem = Record<string, unknown>;

const toNormalizedString = (value: unknown): string => String(value ?? "").trim();

const hasValidCargoQuantity = (value: unknown): boolean => {
  const trimmed = toNormalizedString(value);
  if (!trimmed) return false;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0;
};

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

const CargoDetails = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const { fields, append, remove } = useFieldArray({
    control: formData.control,
    name: "cargos",
  });

  const { options: cargosRaw } = useFormattedReference<GenericReferenceItem>(
    REFERENCE_DATA_TYPES.CARGOS,
    "bff-gatepass",
  );
  const { options: unitsRaw } = useFormattedReference<GenericReferenceItem>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-gatepass",
  );

  const cargoCatalog = useMemo(
    () =>
      cargosRaw
        .filter((item) => item.active !== false)
        .map((item) => {
          const value = toNormalizedString(item.cargoCode);
          const label = toNormalizedString(item.cargoDescription);

          const cargoTypeObj =
            item.cargoType && typeof item.cargoType === "object"
              ? (item.cargoType as GenericReferenceItem)
              : undefined;

          const cargoTypeCode = toNormalizedString(cargoTypeObj?.cargoTypeCode);
          const cargoTypeLabel = toNormalizedString(cargoTypeObj?.cargoTypeDescription);

          const hazardRaw = item.hazard;
          const isHazard = hazardRaw === true || String(hazardRaw ?? "").toLowerCase() === "true";
          if (!value || !label || !cargoTypeCode) return null;
          return { value, label, cargoTypeCode, cargoTypeLabel, isHazard };
        })
        .filter(
          (
            item,
          ): item is {
            value: string;
            label: string;
            cargoTypeCode: string;
            cargoTypeLabel: string;
            isHazard: boolean;
          } => Boolean(item),
        ),
    [cargosRaw],
  );

  const cargoTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    return cargoCatalog
      .map((item) => {
        if (seen.has(item.cargoTypeCode)) return null;
        seen.add(item.cargoTypeCode);
        return {
          value: item.cargoTypeCode,
          label: item.cargoTypeLabel || item.cargoTypeCode,
        };
      })
      .filter((item): item is { value: string; label: string } => Boolean(item));
  }, [cargoCatalog]);

  const unitOptions = useMemo(
    () =>
      unitsRaw
        .filter((item) => {
          const isCargoUnit = item.isCargoUnit;
          return isCargoUnit === true || String(isCargoUnit ?? "").toLowerCase() === "true";
        })
        .map((item) => {
          const value = item.unitCode;
          const label = item.unitDescription;
          return value && label ? { value, label } : null;
        })
        .filter((item): item is { value: string; label: string } => Boolean(item)),
    [unitsRaw],
  );

  const formState = useFormState({ control: formData.control });
  const { submitCount, isSubmitted } = formState;
  const hasAttemptedSubmit = submitCount > 0 || isSubmitted;
  const { clearErrors, setError, getValues, getFieldState } = formData;

  const syncQuantityUnitPairValidation = useCallback(
    (index: number) => {
      const quantityPath = `cargos.${index}.quantity` as const;
      const unitPath = `cargos.${index}.unitOfMeasurement` as const;
      const cargo = getValues(`cargos.${index}` as const);
      const hazardRow = hasActiveHazardCargo([cargo]);
      const hasQuantity = hasValidCargoQuantity(cargo?.quantity);
      const hasUnit = Boolean(toNormalizedString(cargo?.unitOfMeasurement));

      const quantityRequiredMessage = t("form-validation.required", {
        label: t(`${TRANSLATION_KEY}.quantity`),
      });
      const unitRequiredMessage = t("form-validation.required", {
        label: t(`${TRANSLATION_KEY}.unit-of-measurement`),
      });

      const ensureRequiredError = (
        path: typeof quantityPath | typeof unitPath,
        message: string,
      ) => {
        const currentError = getFieldState(path, formState).error;
        if (currentError?.type === "required" && currentError.message === message) {
          return;
        }
        setError(path, { type: "required", message });
      };

      const ensureNoError = (path: typeof quantityPath | typeof unitPath) => {
        const currentError = getFieldState(path, formState).error;
        if (!currentError) return;
        clearErrors(path);
      };

      if (hazardRow) {
        if (!hasQuantity && !hasUnit) {
          ensureNoError(quantityPath);
          ensureNoError(unitPath);
          return;
        }
        if (hasQuantity && !hasUnit) {
          ensureRequiredError(unitPath, unitRequiredMessage);
          ensureNoError(quantityPath);
          return;
        }
        if (hasUnit && !hasQuantity) {
          ensureRequiredError(quantityPath, quantityRequiredMessage);
          ensureNoError(unitPath);
          return;
        }
        ensureNoError(quantityPath);
        ensureNoError(unitPath);
        return;
      }

      if (hasQuantity && !hasUnit) {
        ensureRequiredError(unitPath, unitRequiredMessage);
        ensureNoError(quantityPath);
        return;
      }
      if (hasUnit && !hasQuantity) {
        ensureRequiredError(quantityPath, quantityRequiredMessage);
        ensureNoError(unitPath);
        return;
      }
      if (hasQuantity && hasUnit) {
        ensureNoError(quantityPath);
        ensureNoError(unitPath);
        return;
      }
      if (hasAttemptedSubmit) {
        ensureRequiredError(quantityPath, quantityRequiredMessage);
        ensureRequiredError(unitPath, unitRequiredMessage);
        return;
      }

      ensureNoError(quantityPath);
      ensureNoError(unitPath);
    },
    [clearErrors, formState, getFieldState, getValues, hasAttemptedSubmit, setError],
  );

  const handleHazardFlagChange = useCallback(
    (index: number, wasHazard: boolean, isHazard: boolean) => {
      if (wasHazard === isHazard) return;
      syncQuantityUnitPairValidation(index);
    },
    [syncQuantityUnitPairValidation],
  );

  const cargos = formData.watch("cargos") ?? [];

  const isHazardCargoRow = (index: number): boolean => {
    const cargo = cargos[index];
    return hasActiveHazardCargo([cargo]);
  };

  const hasHazardCargo = hasActiveHazardCargo(cargos);
  const disableAddCargo = hasHazardCargo;

  const syncCargosToStore = () => {
    updateFormData("cargos", formData.getValues("cargos"));
  };

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.cargo-details`)}
        button={
          SHOW_MULTI_CARGO_CONTROLS ? (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              disabled={disableAddCargo}
              label={
                <span className="normal-case font-medium">{t(`${TRANSLATION_KEY}.add-cargo`)}</span>
              }
              testId="truck-cargo-add"
              onClick={() => {
                append({
                  cargoType: "",
                  cargo: "",
                  hazardCargo: false,
                  quantity: "",
                  unitOfMeasurement: "",
                });
                syncCargosToStore();
              }}
            />
          ) : undefined
        }
      >
        <div className="col-span-2 flex flex-col gap-6">
          {fields.map((field, index) => {
            const selectedCargoType = toNormalizedString(cargos[index]?.cargoType);
            const hazardRow = isHazardCargoRow(index);
            const cargoQuantity = toNormalizedString(cargos[index]?.quantity);
            const cargoUnit = toNormalizedString(cargos[index]?.unitOfMeasurement);
            const hasCargoQuantity = Boolean(cargoQuantity);
            const hasCargoUnit = Boolean(cargoUnit);
            const filteredCargoOptions = cargoCatalog
              .filter((item) => !selectedCargoType || item.cargoTypeCode === selectedCargoType)
              .map((item) => ({ value: item.value, label: item.label }));

            return (
              <div key={field.id} className="rounded border border-gray-200 p-4">
                {SHOW_MULTI_CARGO_CONTROLS && (
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-sm font-semibold">
                      {t(`${TRANSLATION_KEY}.cargo-type-header`, { n: index + 1 })}
                    </h4>
                    {fields.length > 1 && (
                      <RemoveButton
                        startIcon={<CloseIcon />}
                        variant="contained"
                        disableElevation
                        className="normal-case"
                        label={<span className="normal-case font-medium">Remove</span>}
                        testId={`truck-cargo-remove-${index}`}
                        onClick={() => {
                          remove(index);
                          syncCargosToStore();
                        }}
                      />
                    )}
                  </div>
                )}

                {hazardRow && (
                  <div className="col-span-2 mb-4">
                    <Alert
                      severity="warning"
                      icon={<WarningAmberIcon color="warning" fontSize="inherit" />}
                      data-testid={`truck-cargo-hazard-warning-${index}`}
                    >
                      {t(`${TRANSLATION_KEY}.hazard-cargo-single-warning`)}
                    </Alert>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormItem
                    required
                    name={`cargos.${index}.cargoType`}
                    type="autocomplete"
                    label={t(`${TRANSLATION_KEY}.cargo-type`)}
                    options={cargoTypeOptions}
                    testId={`truck-cargo-type-${index}`}
                    onFieldChange={() => {
                      const wasHazard = isHazardCargoRow(index);
                      formData.setValue(`cargos.${index}.cargo` as const, "", {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      formData.setValue(`cargos.${index}.hazardCargo` as const, false, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      handleHazardFlagChange(index, wasHazard, false);
                      syncCargosToStore();
                    }}
                  />

                  <FormItem
                    required
                    name={`cargos.${index}.cargo`}
                    type="autocomplete"
                    label={t(`${TRANSLATION_KEY}.cargo`)}
                    options={filteredCargoOptions}
                    testId={`truck-cargo-name-${index}`}
                    onFieldChange={() => {
                      const wasHazard = isHazardCargoRow(index);
                      const currentCargoCode = toNormalizedString(
                        formData.getValues(`cargos.${index}.cargo` as const),
                      );
                      const currentCargo = cargoCatalog.find(
                        (item) => item.value === currentCargoCode,
                      );
                      const hazard = currentCargo?.isHazard === true;

                      if (!currentCargoCode) {
                        formData.setValue(`cargos.${index}.hazardCargo` as const, false, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        handleHazardFlagChange(index, wasHazard, false);
                        syncCargosToStore();
                        return;
                      }

                      formData.setValue(`cargos.${index}.hazardCargo` as const, hazard, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      handleHazardFlagChange(index, wasHazard, hazard);
                      syncCargosToStore();
                    }}
                  />

                  <FormItem
                    required
                    name={`cargos.${index}.hazardCargo`}
                    type="checkbox"
                    label={t(`${TRANSLATION_KEY}.hazard-cargo`)}
                    testId={`truck-cargo-hazard-${index}`}
                    disabled
                    onFieldChange={syncCargosToStore}
                  />

                  <div className="hidden md:block" />

                  <FormItem
                    required={!hazardRow || hasCargoUnit}
                    name={`cargos.${index}.quantity`}
                    type="number"
                    label={t(`${TRANSLATION_KEY}.quantity`)}
                    decimals={4}
                    testId={`truck-cargo-quantity-${index}`}
                    onFieldChange={() => {
                      syncCargosToStore();
                      syncQuantityUnitPairValidation(index);
                    }}
                  />

                  <FormItem
                    required={!hazardRow || hasCargoQuantity}
                    name={`cargos.${index}.unitOfMeasurement`}
                    type="autocomplete"
                    label={t(`${TRANSLATION_KEY}.unit-of-measurement`)}
                    options={unitOptions}
                    testId={`truck-cargo-unit-${index}`}
                    onFieldChange={() => {
                      syncCargosToStore();
                      syncQuantityUnitPairValidation(index);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default CargoDetails;
